import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { AuthProvider } from '@contexts/AuthContext';
import { ProfileProvider } from '@contexts/ProfileContext';
import { ActivityModalProvider } from '@contexts/ActivityModalContext';
import { loadConfig } from './config';

// Components
import { Login } from '@components/Login';
import { AuthCallback } from '@components/AuthCallback';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { DashboardLayout } from '@components/DashboardLayout';
import { PermissionGuard } from '@components/PermissionGuard';
import { FeatureGuard } from '@components/FeatureGuard';

// Pages
import { DashboardView } from '@pages/dashboard/DashboardView';
import { KanbanView } from '@pages/kanban/KanbanView';
import { CalendarView } from '@pages/calendar/CalendarView';
import { ActivityListView } from '@pages/activities/ActivityListView';
import { RecurrenceListView } from '@pages/recurrences/RecurrenceListView';
import { BoardAdminView } from '@pages/admin/BoardAdminView';
import { UsersAdminView } from '@pages/admin/UsersAdminView';
import { SettingsAdminView } from '@pages/admin/SettingsAdminView';
import { DocumentsView } from '@pages/documents/DocumentsView';
import { FirearmListView } from '@pages/cac/FirearmListView';
import { CacDocumentListView } from '@pages/cac/CacDocumentListView';
import { ExpiringView } from '@pages/cac/ExpiringView';
import { HelpCenterView } from '@pages/help/HelpCenterView';
import { AccountsView } from '@pages/finance/AccountsView';
import { TransactionsView } from '@pages/finance/TransactionsView';
import { CategoriesView } from '@pages/finance/CategoriesView';
import { ImportView } from '@pages/finance/ImportView';
import { ImportRulesView } from '@pages/finance/ImportRulesView';
import { CardsView } from '@pages/finance/CardsView';
import { RecurringView } from '@pages/finance/RecurringView';
import { ProjectionView } from '@pages/finance/ProjectionView';
import { BudgetView } from '@pages/finance/BudgetView';
import { ReportsView } from '@pages/finance/ReportsView';

const AccessDenied: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-24">
    <div className="text-center">
      <Lock size={48} className="text-slate-300 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-slate-700 mb-2">Acesso restrito</h2>
      <p className="text-slate-500">{message ?? 'Você não tem permissão para acessar esta página.'}</p>
    </div>
  </div>
);

/** Rota protegida por permissão (permission-config.json). */
const Guarded: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => (
  <PermissionGuard permission={permission} fallback={<AccessDenied />}>
    {children}
  </PermissionGuard>
);

/** Rota do Acervo CAC: permissão + módulo liberado (`features.cac` do perfil). */
const CacGuarded: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => (
  <Guarded permission={permission}>
    <FeatureGuard feature="cac">{children}</FeatureGuard>
  </Guarded>
);

const AppContent: React.FC = () => (
  <Router>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProfileProvider>
              <ActivityModalProvider>
                <DashboardLayout />
              </ActivityModalProvider>
            </ProfileProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Guarded permission="agenda.dashboard.read"><DashboardView /></Guarded>} />
        <Route path="kanban" element={<Guarded permission="agenda.activities.read"><KanbanView /></Guarded>} />
        <Route path="kanban/:boardId" element={<Guarded permission="agenda.activities.read"><KanbanView /></Guarded>} />
        <Route path="calendar" element={<Guarded permission="agenda.activities.read"><CalendarView /></Guarded>} />
        <Route path="activities" element={<Guarded permission="agenda.activities.read"><ActivityListView /></Guarded>} />
        <Route path="recurrences" element={<Guarded permission="agenda.recurrences.manage"><RecurrenceListView /></Guarded>} />
        <Route path="boards" element={<Guarded permission="agenda.boards.manage"><BoardAdminView /></Guarded>} />
        <Route path="documents" element={<Guarded permission="documentos.read"><DocumentsView /></Guarded>} />
        <Route path="cac/firearms" element={<CacGuarded permission="acervo.read"><FirearmListView /></CacGuarded>} />
        <Route path="cac/documents" element={<CacGuarded permission="acervo.read"><CacDocumentListView /></CacGuarded>} />
        <Route path="cac/expiring" element={<CacGuarded permission="acervo.read"><ExpiringView /></CacGuarded>} />
        <Route path="finance/accounts" element={<Guarded permission="financas.read"><AccountsView /></Guarded>} />
        <Route path="finance/cards" element={<Guarded permission="financas.read"><CardsView /></Guarded>} />
        <Route path="finance/transactions" element={<Guarded permission="financas.read"><TransactionsView /></Guarded>} />
        <Route path="finance/recurring" element={<Guarded permission="financas.read"><RecurringView /></Guarded>} />
        <Route path="finance/projection" element={<Guarded permission="financas.read"><ProjectionView /></Guarded>} />
        <Route path="finance/budget" element={<Guarded permission="financas.read"><BudgetView /></Guarded>} />
        <Route path="finance/reports" element={<Guarded permission="financas.read"><ReportsView /></Guarded>} />
        <Route path="finance/categories" element={<Guarded permission="financas.read"><CategoriesView /></Guarded>} />
        <Route path="finance/import" element={<Guarded permission="financas.import"><ImportView /></Guarded>} />
        <Route path="finance/import-rules" element={<Guarded permission="financas.import"><ImportRulesView /></Guarded>} />
        <Route path="admin/users" element={<Guarded permission="admin.users.manage"><UsersAdminView /></Guarded>} />
        <Route path="admin/settings" element={<Guarded permission="admin.settings.manage"><SettingsAdminView /></Guarded>} />
        <Route path="help" element={<HelpCenterView />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

const AppLoading: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mx-auto mb-4">
        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-slate-500">Carregando configurações…</p>
    </div>
  </div>
);

const AppError: React.FC<{ error: string }> = ({ error }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-card p-8 max-w-md text-center">
      <h1 className="text-h3 mb-2">Não foi possível iniciar</h1>
      <p className="text-slate-500 mb-4">{error}</p>
      <button className="btn-primary" onClick={() => window.location.reload()}>
        Tentar novamente
      </button>
    </div>
  </div>
);

// App wrapper com providers e configuração
export default function App() {
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const [configError, setConfigError] = React.useState<string | null>(null);

  useEffect(() => {
    loadConfig()
      .then(() => setConfigLoaded(true))
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Erro desconhecido';
        setConfigError(`Falha ao carregar configurações: ${message}`);
      });
  }, []);

  if (!configLoaded && !configError) return <AppLoading />;
  if (configError) return <AppError error={configError} />;

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
